/**
 * Portfolio Kit — curriculum modules (Phase C).
 *
 * The 4 modules teach the portfolio-build journey (choose an archetype → write a
 * production-grade README → prove it works → ship & demo). Content lives as clean
 * markdown files under `public/portfolio-modules/` and is fetched at runtime and
 * rendered via <LessonMarkdown> — same renderer as the Agentic Mastery lessons.
 * (The content is educational and no more sensitive than the rest of the public
 * frontend bundle, so serving it from `public/` is fine.)
 */

import { Localized, L } from "../utils/localized";

export interface PortfolioModule {
  /** URL slug — `/portfolio-kit/modules/:slug`. */
  slug: string;
  /** Short number label, e.g. "M1". */
  number: string;
  title: string;
  /** One-line hook shown on the module card. */
  tagline: string;
  /** Approximate read time in minutes (for the card). */
  readMinutes: number;
  /** Path to the markdown file under public/. */
  mdPath: string;
}

export const PORTFOLIO_MODULES: Localized<PortfolioModule[]> = {
  en: [
    {
      slug: "choosing-scoping-archetype",
      number: "M1",
      title: "Choosing & Scoping Your Portfolio Archetype",
      tagline:
        "Pick one project from the 10 archetypes and scope it to a v1 you'll actually finish.",
      readMinutes: 9,
      mdPath: "/portfolio-modules/m1-choosing-scoping-archetype.md",
    },
    {
      slug: "production-readme-7-domain",
      number: "M2",
      title: "Writing a Production-Grade README (the 7-Domain Framework)",
      tagline:
        "Turn a working project into a recruiter-legible README — and write to the rubric that scores it.",
      readMinutes: 11,
      mdPath: "/portfolio-modules/m2-production-readme-7-domain.md",
    },
    {
      slug: "evals-observability-deployment",
      number: "M3",
      title: "Proving It Works: Evals, Observability & Deployment",
      tagline:
        "Replace adjectives with evidence: an eval table, public traces, and a passed deployment checklist.",
      readMinutes: 11,
      mdPath: "/portfolio-modules/m3-evals-observability-deployment.md",
    },
    {
      slug: "shipping-demoing-recruiters",
      number: "M4",
      title: "Shipping & Demoing to Recruiters",
      tagline:
        "Deploy the site, record the walkthrough, and position for the recruiter's 90-second scan.",
      readMinutes: 10,
      mdPath: "/portfolio-modules/m4-shipping-demoing-recruiters.md",
    },
  ],
  es: [
    {
      "slug": "choosing-scoping-archetype",
      "number": "M1",
      "title": "Elección y definición del alcance de tu arquetipo de portafolio",
      "tagline": "Elige un proyecto de los 10 arquetipos y define su alcance a una v1 que realmente vayas a terminar.",
      "readMinutes": 9,
      "mdPath": "/portfolio-modules/m1-choosing-scoping-archetype.md"
    },
    {
      "slug": "production-readme-7-domain",
      "number": "M2",
      "title": "Cómo escribir un README de nivel producción (el marco de los 7 dominios)",
      "tagline": "Convierte un proyecto funcional en un README legible para reclutadores, y escríbelo según la rúbrica que lo evalúa.",
      "readMinutes": 11,
      "mdPath": "/portfolio-modules/m2-production-readme-7-domain.md"
    },
    {
      "slug": "evals-observability-deployment",
      "number": "M3",
      "title": "Demuestra que funciona: evaluaciones, observabilidad y despliegue",
      "tagline": "Reemplaza los adjetivos con evidencia: una tabla de evaluaciones, trazas públicas y una lista de verificación de despliegue superada.",
      "readMinutes": 11,
      "mdPath": "/portfolio-modules/m3-evals-observability-deployment.md"
    },
    {
      "slug": "shipping-demoing-recruiters",
      "number": "M4",
      "title": "Publicación y demostración ante reclutadores",
      "tagline": "Despliega el sitio, graba el recorrido y posiciónate para el escaneo de 90 segundos del reclutador.",
      "readMinutes": 10,
      "mdPath": "/portfolio-modules/m4-shipping-demoing-recruiters.md"
    }
  ],
};

// Returns the whole module object (title/tagline are user-facing prose), so
// this needs locale + L() resolution — unlike a slug-only existence check.
export const getPortfolioModuleBySlug = (
  slug: string | undefined,
  locale: string = "en",
): PortfolioModule | undefined =>
  L(PORTFOLIO_MODULES, locale).find((m) => m.slug === slug);

/** Fetch a module's markdown body from public/. Throws on a non-OK response. */
export async function fetchPortfolioModuleMarkdown(
  mdPath: string,
): Promise<string> {
  const res = await fetch(mdPath);
  if (!res.ok) {
    throw new Error(`Failed to load module (${res.status})`);
  }
  return res.text();
}
